// Bottom Header com seleção de empresa, busca e ações
import { useState, useEffect, useRef } from 'react';
import { Menu, Moon, Sun, Bell, Building, ChevronDown } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { CommandPalette } from '../common/CommandPalette';

interface BottomHeaderProps {
  onMenuClick: () => void;
}

export function BottomHeader({ onMenuClick }: BottomHeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { empresaSelecionada, empresas, setEmpresaSelecionada, loading: empresaLoading } = useEmpresa();
  const [showEmpresaDropdown, setShowEmpresaDropdown] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const empresaDropdownRef = useRef<HTMLDivElement>(null);

  const handleSelectEmpresa = async (empresa: any) => {
    await setEmpresaSelecionada(empresa);
    setShowEmpresaDropdown(false);
  };

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (empresaDropdownRef.current && !empresaDropdownRef.current.contains(event.target as Node)) {
        setShowEmpresaDropdown(false);
      }
    };

    if (showEmpresaDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showEmpresaDropdown]);

  // Atalho de teclado para Command Palette (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setShowCommandPalette(true);
      }
      if (event.key === 'Escape') {
        setShowCommandPalette(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header className="fixed bottom-0 left-0 right-0 z-[40] bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 shadow-lg shadow-black/5 dark:shadow-black/20">
        <div className="flex items-center justify-between h-16 px-4 md:px-6 gap-4">
          {/* Menu Mobile - Esquerda */}
          <div className="flex items-center lg:hidden">
            <button
              onClick={onMenuClick}
              className="p-2 hover:bg-gray-100/60 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 ease-out"
            >
              <Menu className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          </div>

          {/* Seletor de Empresa - Esquerda (desktop) */}
          {empresaSelecionada && !empresaLoading && (
            <div className="relative hidden lg:block" ref={empresaDropdownRef}>
              <button
                onClick={() => setShowEmpresaDropdown(!showEmpresaDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 ease-out"
              >
                <Building className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100 max-w-[150px] truncate">
                  {empresaSelecionada.nomeFantasia}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              </button>

              {showEmpresaDropdown && empresas.length > 1 && (
                <div className="absolute bottom-full left-0 mb-2 w-64 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-800 z-[55]">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-800 mb-2">
                      Empresas
                    </div>
                    {empresas.map((empresa) => (
                      <button
                        key={empresa.id}
                        onClick={() => handleSelectEmpresa(empresa)}
                        className={`w-full px-3 py-2 rounded-lg text-left transition-all duration-200 ease-out mb-1 last:mb-0 ${empresaSelecionada.id === empresa.id
                          ? 'bg-primary-100/30 dark:bg-primary-900/15 text-primary-700 dark:text-primary-300'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100'
                          }`}
                      >
                        <div className="font-medium text-sm">{empresa.nomeFantasia}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{empresa.cnpj}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Command Palette - Centro */}
          <div className="flex-1 flex justify-center max-w-2xl">
            <button
              onClick={() => setShowCommandPalette(true)}
              className="w-full max-w-md flex items-center gap-3 px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 ease-out text-left"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm text-gray-500 dark:text-gray-400 flex-1">
                Buscar notas, clientes, produtos...
              </span>
              <kbd className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded">
                {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} K
              </kbd>
            </button>
          </div>

          {/* Actions - Direita */}
          <div className="flex items-center gap-2">
            {/* Notificações */}
            <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-refined relative">
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* Toggle Theme */}
            <button
              onClick={toggleTheme}
              className="p-2 hover:bg-gray-100/60 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 ease-out"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              ) : (
                <Moon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              )}
            </button>

            {/* Email do Usuário */}
            {user && (
              <div className="hidden md:flex items-center gap-2 px-3 py-2">
                <span className="text-sm text-gray-600 dark:text-gray-300 truncate max-w-[200px]">
                  {user.email}
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Command Palette Modal */}
      <CommandPalette isOpen={showCommandPalette} onClose={() => setShowCommandPalette(false)} />
    </>
  );
}

